// Typed bindings over the generated OpenAPI client (./schema.ts).
//
// schema.ts is produced by `make openapi` (openapi-typescript, version pinned)
// and committed — never hand-edit it. These helpers layer end-to-end request /
// response types onto the shared fetch transport in services/api.ts, so the SPA
// and the Go API cannot drift: a contract change regenerates schema.ts and any
// mismatch surfaces as a TypeScript error here.

import { api, apiDownload, type DownloadResult } from '../services/api';
import { recaptchaHeader } from '../services/captcha';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];
export type HealthStatus = Schemas['HealthStatus'];
export type Me = Schemas['Me'];
export type SyncRequest = Schemas['SyncRequest'];
export type SyncResponse = Schemas['SyncResponse'];
export type SyncMutation = Schemas['SyncMutation'];
export type SyncResult = Schemas['SyncResult'];
export type ContactRequest = Schemas['ContactRequest'];
export type ContactResult = Schemas['ContactResult'];
export type Product = Schemas['Product'];
export type ProductDetail = Schemas['ProductDetail'];
export type ProductList = Schemas['ProductList'];
export type Cart = Schemas['Cart'];
export type CartLine = Schemas['CartLine'];
export type CartItemRequest = Schemas['CartItemRequest'];
export type Order = Schemas['Order'];
export type OrderLine = Schemas['OrderLine'];
export type Amounts = Schemas['Amounts'];
export type Address = Schemas['Address'];
export type OrderStatus = Schemas['OrderStatus'];
export type CheckoutRequest = Schemas['CheckoutRequest'];
export type OrderList = Schemas['OrderList'];
export type AccountProfile = Schemas['AccountProfile'];
export type AccountUpdate = Schemas['AccountUpdate'];
export type ConsentRecord = Schemas['ConsentRecord'];
export type ConsentList = Schemas['ConsentList'];
export type AdminProduct = Schemas['AdminProduct'];
export type AdminProductCreate = Schemas['AdminProductCreate'];
export type AdminProductUpdate = Schemas['AdminProductUpdate'];

// Derived from the generated paths map (not the bare schema) so the binding
// breaks if the operation, status code, or media type ever changes.
type HealthzResponse = paths['/healthz']['get']['responses'][200]['content']['application/json'];

export function getHealthz(signal?: AbortSignal): Promise<HealthzResponse> {
  return api<HealthzResponse>('/healthz', { signal });
}

// postSync pushes a batch of queued offline mutations to the receiver,
// which resolves each last-write-wins and returns one outcome per mutation. The
// request/response types are pinned to the generated paths map, so a contract
// change surfaces here as a type error.
type SyncBody = paths['/api/sync']['post']['requestBody']['content']['application/json'];
type SyncResp = paths['/api/sync']['post']['responses'][200]['content']['application/json'];

export function postSync(body: SyncBody, signal?: AbortSignal): Promise<SyncResp> {
  return api<SyncResp>('/api/sync', { method: 'POST', body, signal });
}

// postContact submits the public contact form. The request/response types are
// pinned to the generated paths map, so a contract change surfaces here as a
// type error.
type ContactBody = paths['/api/contact']['post']['requestBody']['content']['application/json'];
type ContactResp = paths['/api/contact']['post']['responses'][201]['content']['application/json'];

export async function postContact(body: ContactBody, signal?: AbortSignal): Promise<ContactResp> {
  return api<ContactResp>('/api/contact', {
    method: 'POST',
    body,
    signal,
    headers: await recaptchaHeader('contact'),
  });
}

// --- Shop catalog ---
// listShopProducts lists the public catalog, optionally filtered by category.
type ProductsResp = paths['/api/shop/products']['get']['responses'][200]['content']['application/json'];

export function listShopProducts(
  params: { category?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<ProductsResp> {
  const qs = new URLSearchParams();
  if (params.category) qs.set('category', params.category);
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api<ProductsResp>(`/api/shop/products${suffix}`, { signal });
}

// getShopProduct loads one product by its public slug.
type ProductResp = paths['/api/shop/products/{slug}']['get']['responses'][200]['content']['application/json'];

export function getShopProduct(slug: string, signal?: AbortSignal): Promise<ProductResp> {
  return api<ProductResp>(`/api/shop/products/${encodeURIComponent(slug)}`, { signal });
}

// getShopCart returns the authenticated caller's cart.
type CartResp = paths['/api/shop/cart']['get']['responses'][200]['content']['application/json'];

export function getShopCart(signal?: AbortSignal): Promise<CartResp> {
  return api<CartResp>('/api/shop/cart', { signal });
}

// updateShopCart adds or updates a single cart line item (quantity 0 removes it).
type CartUpdateBody = paths['/api/shop/cart']['post']['requestBody']['content']['application/json'];
type CartUpdateResp = paths['/api/shop/cart']['post']['responses'][200]['content']['application/json'];

export function updateShopCart(body: CartUpdateBody, signal?: AbortSignal): Promise<CartUpdateResp> {
  return api<CartUpdateResp>('/api/shop/cart', { method: 'POST', body, signal });
}

// --- Checkout & orders ---
// placeOrder turns the authenticated customer's cart into one placed order.
// Account-gated; success is 201. idempotencyKey (optional) dedups a retry so a
// double-submit returns the one order. Errors surface as ApiError: 400 (invalid
// body), 401, 409 (CART_EMPTY / OUT_OF_STOCK), 429, 503.
type CheckoutBody = paths['/api/shop/checkout']['post']['requestBody']['content']['application/json'];
type CheckoutResp = paths['/api/shop/checkout']['post']['responses'][201]['content']['application/json'];

export function placeOrder(body: CheckoutBody, idempotencyKey?: string, signal?: AbortSignal): Promise<CheckoutResp> {
  return api<CheckoutResp>('/api/shop/checkout', {
    method: 'POST',
    body,
    signal,
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

// --- Orders + customer account (M4) ---
// getShopOrder reads one of the caller's own orders by order number.
type OrderResp = paths['/api/shop/orders/{orderNo}']['get']['responses'][200]['content']['application/json'];

export function getShopOrder(orderNo: string, signal?: AbortSignal): Promise<OrderResp> {
  return api<OrderResp>(`/api/shop/orders/${encodeURIComponent(orderNo)}`, { signal });
}

// queryShopOrders lists the caller's own orders, newest first. The body is
// optional in the contract but `limit` is required in the generated type, so a
// default is injected here and callers pass only what they override.
type OrdersQueryBody = NonNullable<paths['/api/shop/orders/query']['post']['requestBody']>['content']['application/json'];
type OrdersQueryResp = paths['/api/shop/orders/query']['post']['responses'][200]['content']['application/json'];

export function queryShopOrders(body: Partial<OrdersQueryBody> = {}, signal?: AbortSignal): Promise<OrdersQueryResp> {
  const sent: OrdersQueryBody = { limit: 50, ...body };
  return api<OrdersQueryResp>('/api/shop/orders/query', { method: 'POST', body: sent, signal });
}

// getAccount returns the caller's own profile (email, name, addresses, locale).
type AccountResp = paths['/api/account']['get']['responses'][200]['content']['application/json'];

export function getAccount(signal?: AbortSignal): Promise<AccountResp> {
  return api<AccountResp>('/api/account', { signal });
}

// updateAccount saves the caller's name, saved addresses, and locale.
type AccountUpdateBody = paths['/api/account']['put']['requestBody']['content']['application/json'];

export function updateAccount(body: AccountUpdateBody, signal?: AbortSignal): Promise<AccountResp> {
  return api<AccountResp>('/api/account', { method: 'PUT', body, signal });
}

// getAccountConsent lists the caller's own consent records.
type ConsentResp = paths['/api/account/consent']['get']['responses'][200]['content']['application/json'];

export function getAccountConsent(signal?: AbortSignal): Promise<ConsentResp> {
  return api<ConsentResp>('/api/account/consent', { signal });
}

// --- Admin catalog/inventory management (M5 / SHOP-007) ---
// Gated by the `catalog` capability (store_manager + admin); the server
// re-authorizes every call. Reads list/return the full admin product shape
// (incl. stock + publish state), unlike the public storefront projections.
type AdminProductListResp = paths['/api/catalog/products']['get']['responses'][200]['content']['application/json'];
type AdminProductResp = paths['/api/catalog/products/{slug}']['get']['responses'][200]['content']['application/json'];
type CreatedProductResp = paths['/api/catalog/products']['post']['responses'][201]['content']['application/json'];
type CreateProductBody = paths['/api/catalog/products']['post']['requestBody']['content']['application/json'];
type UpdateProductBody = paths['/api/catalog/products/{slug}']['put']['requestBody']['content']['application/json'];
type PublishBody = paths['/api/catalog/products/{slug}/publish']['post']['requestBody']['content']['application/json'];
type StockBody = paths['/api/catalog/products/{slug}/stock']['post']['requestBody']['content']['application/json'];

export function listAdminProducts(signal?: AbortSignal): Promise<AdminProductListResp> {
  return api<AdminProductListResp>('/api/catalog/products', { signal });
}

export function getAdminProduct(slug: string, signal?: AbortSignal): Promise<AdminProductResp> {
  return api<AdminProductResp>(`/api/catalog/products/${encodeURIComponent(slug)}`, { signal });
}

export function createProduct(body: CreateProductBody, signal?: AbortSignal): Promise<CreatedProductResp> {
  return api<CreatedProductResp>('/api/catalog/products', { method: 'POST', body, signal });
}

export function updateProduct(slug: string, body: UpdateProductBody, signal?: AbortSignal): Promise<AdminProductResp> {
  return api<AdminProductResp>(`/api/catalog/products/${encodeURIComponent(slug)}`, { method: 'PUT', body, signal });
}

export function publishProduct(slug: string, active: boolean, signal?: AbortSignal): Promise<AdminProductResp> {
  const body: PublishBody = { active };
  return api<AdminProductResp>(`/api/catalog/products/${encodeURIComponent(slug)}/publish`, { method: 'POST', body, signal });
}

export function setProductStock(slug: string, stock: number, signal?: AbortSignal): Promise<AdminProductResp> {
  const body: StockBody = { stock };
  return api<AdminProductResp>(`/api/catalog/products/${encodeURIComponent(slug)}/stock`, { method: 'POST', body, signal });
}

// --- Admin orders & fulfillment (M5 / SHOP-008) ---
// Gated by the `orders` capability (store_manager + admin). These see every
// order, unlike the customerId-scoped /api/shop/orders/* reads.
type AllOrdersResp = paths['/api/orders/query']['post']['responses'][200]['content']['application/json'];
type AllOrdersBody = NonNullable<paths['/api/orders/query']['post']['requestBody']>['content']['application/json'];
type AdminOrderResp = paths['/api/orders/{orderNo}']['get']['responses'][200]['content']['application/json'];
type TransitionBody = paths['/api/orders/{orderNo}/transition']['post']['requestBody']['content']['application/json'];
type OrderTrackingBody = paths['/api/orders/{orderNo}/tracking']['post']['requestBody']['content']['application/json'];

export function queryAllOrders(body: Partial<AllOrdersBody> = {}, signal?: AbortSignal): Promise<AllOrdersResp> {
  const sent: AllOrdersBody = { limit: 50, ...body };
  return api<AllOrdersResp>('/api/orders/query', { method: 'POST', body: sent, signal });
}

export function getAdminOrder(orderNo: string, signal?: AbortSignal): Promise<AdminOrderResp> {
  return api<AdminOrderResp>(`/api/orders/${encodeURIComponent(orderNo)}`, { signal });
}

export function transitionOrder(orderNo: string, to: OrderStatus, reason?: string, signal?: AbortSignal): Promise<AdminOrderResp> {
  const body: TransitionBody = reason ? { to, reason } : { to };
  return api<AdminOrderResp>(`/api/orders/${encodeURIComponent(orderNo)}/transition`, { method: 'POST', body, signal });
}

export function setAdminOrderTracking(orderNo: string, trackingCode: string, signal?: AbortSignal): Promise<AdminOrderResp> {
  const body: OrderTrackingBody = { trackingCode };
  return api<AdminOrderResp>(`/api/orders/${encodeURIComponent(orderNo)}/tracking`, { method: 'POST', body, signal });
}

// --- Admin customers directory (M5 / SHOP-009) ---
// Gated by the `customers` capability. The directory + detail are read for any
// staff who can view customers; setCustomerStatus (deactivate / reactivate) is
// the one write — the server requires edit on customers (admin-only) AND a
// desktop device class, so the detail's danger zone mirrors that with
// can('customers', 'edit') and a confirm (#129, SHOP-009).
export type CustomerSummary = Schemas['CustomerSummary'];
export type CustomerDetail = Schemas['CustomerDetail'];

type CustomersListResp = paths['/api/customers/query']['post']['responses'][200]['content']['application/json'];
type CustomersQueryBody = NonNullable<paths['/api/customers/query']['post']['requestBody']>['content']['application/json'];
type CustomerDetailResp = paths['/api/customers/{userId}']['get']['responses'][200]['content']['application/json'];
type CustomerStatusResp = paths['/api/customers/{userId}/status']['post']['responses'][200]['content']['application/json'];
type CustomerStatusBody = NonNullable<paths['/api/customers/{userId}/status']['post']['requestBody']>['content']['application/json'];

export function queryCustomers(body: Partial<CustomersQueryBody> = {}, signal?: AbortSignal): Promise<CustomersListResp> {
  const sent: CustomersQueryBody = { limit: 50, ...body };
  return api<CustomersListResp>('/api/customers/query', { method: 'POST', body: sent, signal });
}

export function getCustomerDetail(userID: string, signal?: AbortSignal): Promise<CustomerDetailResp> {
  return api<CustomerDetailResp>(`/api/customers/${encodeURIComponent(userID)}`, { signal });
}

// setCustomerStatus activates (active=true) or deactivates (active=false) a
// customer. Admin-only + desktop-only server-side; returns the updated summary.
export function setCustomerStatus(userID: string, active: boolean, signal?: AbortSignal): Promise<CustomerStatusResp> {
  const body: CustomerStatusBody = { active };
  return api<CustomerStatusResp>(`/api/customers/${encodeURIComponent(userID)}/status`, { method: 'POST', body, signal });
}

// --- Admin store settings (M5 / SHOP-011) — admin-only ---
export type StoreSettings = Schemas['StoreSettings'];

type StoreSettingsResp = paths['/api/admin/settings']['get']['responses'][200]['content']['application/json'];
type StoreSettingsBody = paths['/api/admin/settings']['put']['requestBody']['content']['application/json'];

export function getStoreSettings(signal?: AbortSignal): Promise<StoreSettingsResp> {
  return api<StoreSettingsResp>('/api/admin/settings', { signal });
}

export function updateStoreSettings(body: StoreSettingsBody, signal?: AbortSignal): Promise<StoreSettingsResp> {
  return api<StoreSettingsResp>('/api/admin/settings', { method: 'PUT', body, signal });
}

// --- App-level health state, surfaced by the status pill (components/HealthPill). ---

export type HealthState =
  | { kind: 'unknown' }
  | { kind: 'ok'; health: HealthStatus }
  | { kind: 'degraded'; health: HealthStatus }
  | { kind: 'error' };

export const HEALTH_EVENT = 'app:health';
const FRESH_MS = 30_000;

let last: HealthState = { kind: 'unknown' };
let lastAt = 0;
let inflight: Promise<HealthState> | null = null;

export function getLastHealth(): HealthState {
  return last;
}

function publish(next: HealthState): HealthState {
  last = next;
  document.documentElement.dataset.health = next.kind;
  window.dispatchEvent(new CustomEvent<HealthState>(HEALTH_EVENT, { detail: next }));
  return next;
}

// Pings /healthz and broadcasts the result. Coalesces concurrent callers (one
// in-flight request) and serves a cached result within FRESH_MS so per-route
// remounts of the pill don't spam the endpoint. Pass force to bypass the cache.
export function checkHealth(force = false): Promise<HealthState> {
  if (inflight) return inflight;
  if (!force && last.kind !== 'unknown' && Date.now() - lastAt < FRESH_MS) {
    return Promise.resolve(last);
  }
  inflight = getHealthz()
    .then((health) =>
      publish(health.status === 'ok' ? { kind: 'ok', health } : { kind: 'degraded', health }),
    )
    .catch(() => publish({ kind: 'error' }))
    .finally(() => {
      lastAt = Date.now();
      inflight = null;
    });
  return inflight;
}

// --- Admin visibility console (FE-006a) — desktop-only, console×view ---
export type SystemStatus = Schemas['SystemStatus'];
export type AuditEntry = Schemas['AuditEntry'];
export type SignInEvent = Schemas['SignInEvent'];

type ActivityBody = NonNullable<paths['/api/console/activity']['post']['requestBody']>['content']['application/json'];
type ActivityResp = paths['/api/console/activity']['post']['responses'][200]['content']['application/json'];
type SignInsBody = NonNullable<paths['/api/console/activity/signins']['post']['requestBody']>['content']['application/json'];
type SignInsResp = paths['/api/console/activity/signins']['post']['responses'][200]['content']['application/json'];

export function getConsoleStatus(signal?: AbortSignal): Promise<SystemStatus> {
  return api<SystemStatus>('/api/console/status', { signal });
}

export function getConsoleActivity(body: ActivityBody = {}, signal?: AbortSignal): Promise<ActivityResp> {
  return api<ActivityResp>('/api/console/activity', { method: 'POST', body, signal });
}

export function getConsoleSignIns(body: SignInsBody = {}, signal?: AbortSignal): Promise<SignInsResp> {
  return api<SignInsResp>('/api/console/activity/signins', { method: 'POST', body, signal });
}

// Commerce metrics — the Overview dashboard (FE-006c / SHOP-010), admin-only.
export type StoreMetrics = Schemas['StoreMetrics'];

type MetricsResp = paths['/api/admin/metrics']['get']['responses'][200]['content']['application/json'];

export function getStoreMetrics(range: string, signal?: AbortSignal): Promise<MetricsResp> {
  return api<MetricsResp>(`/api/admin/metrics?range=${encodeURIComponent(range)}`, { signal });
}

// Security + Configuration lenses (FE-006b).
export type SecurityPosture = Schemas['SecurityPosture'];
export type EffectiveConfig = Schemas['EffectiveConfig'];

type UnblockBody = NonNullable<paths['/api/console/security/unblock']['post']['requestBody']>['content']['application/json'];
type UnblockResp = paths['/api/console/security/unblock']['post']['responses'][200]['content']['application/json'];

export function getConsoleSecurity(signal?: AbortSignal): Promise<SecurityPosture> {
  return api<SecurityPosture>('/api/console/security', { signal });
}

export function unblockIp(body: UnblockBody, signal?: AbortSignal): Promise<UnblockResp> {
  return api<UnblockResp>('/api/console/security/unblock', { method: 'POST', body, signal });
}

export function getConsoleConfig(signal?: AbortSignal): Promise<EffectiveConfig> {
  return api<EffectiveConfig>('/api/console/config', { signal });
}

// Compliance lens (GDPR-003) — the data-access summary + the one-click DSAR CSV
// export. Admin-only + desktop-only (the compliance module).
export type ComplianceSummary = Schemas['ComplianceSummary'];

type DsarCsvBody = NonNullable<paths['/api/compliance/dsr/export.csv']['post']['requestBody']>['content']['application/json'];

export function getConsoleCompliance(signal?: AbortSignal): Promise<ComplianceSummary> {
  return api<ComplianceSummary>('/api/console/compliance', { signal });
}

// downloadDsarCsv POSTs {userId} and returns the subject's sectioned CSV as a blob
// + the server-provided filename. It uses the blob transport (not api<T>) because
// the response is text/csv, not JSON, yet the download must still carry the auth +
// CSRF headers.
export function downloadDsarCsv(userId: string, signal?: AbortSignal): Promise<DownloadResult> {
  const body: DsarCsvBody = { userId };
  return apiDownload('/api/compliance/dsr/export.csv', { method: 'POST', body, signal });
}
